import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import { Button } from 'components/UI';
import { iconSuccess } from 'assets';
import { ALL_ACCESS, PREMIUM, FREE, membershipBenefits } from 'utils/constants';

const TierInfoCard = ({
  memberTier,
  monthlyPrice,
  annualPrice,
  isAllAccessEligible,
  showNextScreen,
}) => {
  const navigate = useNavigate();
  const { memberTier: memberTierQuery } = useQuery();
  const [selectedPlan, setSelectedPlan] = useState('annual');

  const showRecommended = isAllAccessEligible
    ? memberTier === ALL_ACCESS
    : memberTier === PREMIUM;

  const isFree = memberTier === FREE;
  const annualMonthlyPrice =
    annualPrice > 0 ? (annualPrice / 12).toFixed(2) : 0;
  const savingsAmount =
    monthlyPrice && annualPrice
      ? (monthlyPrice * 12 - annualPrice).toFixed(0)
      : 0;

  const contentTexts = membershipBenefits[memberTier] || [];

  const handleSignUp = () => {
    if (memberTierQuery !== memberTier) {
      navigate({
        pathname: '/signup',
        search: `?memberTier=${memberTier}`,
      });
    } else {
      showNextScreen();
    }
  };

  const renderPricing = () => {
    if (isFree) {
      return (
        // <div className="flex flex-col items-center mt-4">
        //   <div className="bg-gray-100 rounded-lg px-6 py-4 w-full text-center border border-gray-200">
        //     <span className="text-xl font-bold text-gray-800">$0</span>
        //     <span className="text-sm text-gray-500 ml-1">USD</span>
        //     <p className="text-xs text-gray-400 mt-1">Free forever</p>
        //   </div>
        // </div>
        null
      );
    }

    const isAnnualSelected = selectedPlan === 'annual';

    return (
      <div className="flex flex-col space-y-3 mt-4">
        {/* Annual Plan */}
        <div
          className="rounded-lg px-4 py-3 cursor-pointer"
          style={{
            background: isAnnualSelected
              ? 'rgba(71, 186, 212, 0.08)'
              : '#f7fafc',
            border: isAnnualSelected
              ? '1.5px solid rgba(71, 186, 212, 0.5)'
              : '1.5px solid #e2e8f0',
            transition: 'all 0.2s ease',
          }}
          onClick={() => setSelectedPlan('annual')}
        >
          <div className="flex items-center mb-1">
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{
                color: isAnnualSelected ? '#47bad4' : '#a0aec0',
              }}
            >
              Annual Plan
            </span>
          </div>
          <div className="flex items-baseline">
            <span className="text-2lg font-bold text-gray-800">
              ${annualMonthlyPrice}
            </span>
            <span className="text-sm text-gray-500 ml-1">USD/month</span>
          </div>
          <p className="text-xs text-gray-500 italic mt-1">
            Billed annually (USD {annualPrice}/year)
          </p>
          {Number(savingsAmount) > 0 && (
            <p className="text-xs font-semibold text-easter mt-1">
              Save USD {savingsAmount}/year
            </p>
          )}
        </div>

        {/* Monthly Plan */}
        <div
          className="rounded-lg px-4 py-3 cursor-pointer"
          style={{
            background: !isAnnualSelected
              ? 'rgba(71, 186, 212, 0.08)'
              : '#f7fafc',
            border: !isAnnualSelected
              ? '1.5px solid rgba(71, 186, 212, 0.5)'
              : '1.5px solid #e2e8f0',
            transition: 'all 0.2s ease',
          }}
          onClick={() => setSelectedPlan('monthly')}
        >
          <div className="flex items-center mb-1">
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{
                color: !isAnnualSelected ? '#47bad4' : '#a0aec0',
              }}
            >
              Monthly Plan
            </span>
          </div>
          <div className="flex items-baseline">
            <span className="text-2lg font-bold text-gray-800">
              ${monthlyPrice ? monthlyPrice.toFixed(2) : '0.00'}
            </span>
            <span className="text-sm text-gray-500 ml-1">USD/month</span>
          </div>
          <p className="text-xs text-gray-500 italic mt-1">
            Billed monthly
          </p>
        </div>
      </div>
    );
  };

  const renderBenefitItem = (item, index) => {
    const text = typeof item === 'object' ? item.text : item;
    const isBold = typeof item === 'object' && item.bold;

    return (
      <div className="flex space-x-2" key={index}>
        <img
          alt="check"
          src={iconSuccess.default}
          className="h-3 lg:h-4 mt-1 flex-shrink-0"
        />
        <li className={isBold ? 'font-semibold text-easter' : ''}>
          {text}
        </li>
      </div>
    );
  };

  return (
    <>
      <div className="rounded-lg flex flex-col bg-white md:w-5/12">
        <div className="p-6 flex flex-col justify-between flex-1">
          <div>
            {showRecommended && (
              <div className="bg-yellow-500 text-xs rounded-full py-2 text-center w-1/2 md:w-8/12 mx-auto -mt-10">
                Recommended
              </div>
            )}
            <h4 className="font-semibold text-2lg py-5">{memberTier}</h4>

            {/* Content from membershipBenefits */}
            {contentTexts.length > 0 && (
              <ul className="space-y-3 mt-1 font-light text-sm">
                {contentTexts.map((item, index) =>
                  renderBenefitItem(item, index)
                )}
              </ul>
            )}
          </div>

          {/* Pricing Section */}
          <div>{renderPricing()}</div>
        </div>

        {/* Apply Button */}
        <div className="px-6 pb-6 pt-2">
          <Button
            label={`Apply for ${memberTier}`}
            className="w-full text-xs lg:text-sm"
            onClick={handleSignUp}
          />
        </div>
      </div>
    </>
  );
};

TierInfoCard.propTypes = {
  memberTier: PropTypes.string.isRequired,
  isAllAccessEligible: PropTypes.bool,
  monthlyPrice: PropTypes.number,
  annualPrice: PropTypes.number,
  showNextScreen: PropTypes.func,
};

TierInfoCard.defaultProps = {
  monthlyPrice: 0,
  annualPrice: 0,
  isAllAccessEligible: false,
  showNextScreen: () => {},
};

export default TierInfoCard;
